import React, { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { ArrowLeftIcon, EnvelopeIcon } from '@heroicons/react/24/outline';
import { invitationAPI } from '../../services/api';
import { useToast } from '../Toast';

const InvitationForm = () => {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const queryClient = useQueryClient();

  const [formData, setFormData] = useState({
    email: '',
    role: 'teacher',
    name: ''
  });

  const [errors, setErrors] = useState({});

  const roles = [
    { value: 'admin', label: 'Admin', description: 'Full system access including user management' },
    { value: 'manager', label: 'Manager', description: 'Manage students, batches, and enrollments' },
    { value: 'accountant', label: 'Accountant', description: 'Manage fees, payments, and financial data' },
    { value: 'teacher', label: 'Teacher', description: 'View students and batches, create activities' }
  ];

  const createInvitationMutation = useMutation({
    mutationFn: (data) => invitationAPI.create(data),
    onSuccess: () => {
      showToast('Invitation sent successfully!', 'success');
      queryClient.invalidateQueries(['invitations']);
      queryClient.invalidateQueries(['invitation-stats']);
      navigate('/invitations');
    },
    onError: (error) => {
      const errorMessage = error.response?.data?.detail || 'Failed to send invitation';
      showToast(errorMessage, 'error');
      
      // Handle validation errors
      if (error.response?.data?.detail) {
        const detail = error.response.data.detail;
        if (typeof detail === 'object') {
          setErrors(detail);
        } else {
          setErrors({ general: detail });
        }
      }
    }
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.email) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }

    if (!formData.role) {
      newErrors.role = 'Role is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (validateForm()) {
      createInvitationMutation.mutate(formData);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-8">
      <div className="max-w-lg mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-4">
            <button
              onClick={() => navigate('/invitations')}
              className="inline-flex items-center text-sm font-medium text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 transition-colors duration-200"
            >
              <ArrowLeftIcon className="h-4 w-4 mr-2" />
              Back to Invitations
            </button>
          </div>
          <div className="text-center">
            <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2">
              Send New Invitation
            </h1>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Invite a new user to join the school management system
            </p>
          </div>
        </div>

        {/* Form */}
        <div className="bg-white dark:bg-gray-800 shadow-lg rounded-lg p-6 border border-gray-200 dark:border-gray-700">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Email Field */}
            <div className="space-y-1">
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Email Address *
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                disabled={createInvitationMutation.isLoading}
                className={`w-full px-3 py-2 rounded-md border transition-all duration-200 focus:ring-2 focus:ring-blue-500 focus:outline-none text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 ${
                  errors.email 
                    ? 'border-red-300 dark:border-red-500 bg-red-50 dark:bg-red-900/20' 
                    : 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 hover:border-gray-400 dark:hover:border-gray-500 focus:border-blue-500 dark:focus:border-blue-400'
                } ${createInvitationMutation.isLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
                placeholder="Enter email address"
              />
              {errors.email && (
                <p className="text-xs text-red-600 dark:text-red-400">
                  {errors.email}
                </p>
              )}
            </div>

            {/* Name Field */}
            <div className="space-y-1">
              <label htmlFor="name" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Full Name *
              </label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                disabled={createInvitationMutation.isLoading}
                className={`w-full px-3 py-2 rounded-md border transition-all duration-200 focus:ring-2 focus:ring-blue-500 focus:outline-none text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 ${
                  errors.name 
                    ? 'border-red-300 dark:border-red-500 bg-red-50 dark:bg-red-900/20' 
                    : 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 hover:border-gray-400 dark:hover:border-gray-500 focus:border-blue-500 dark:focus:border-blue-400'
                } ${createInvitationMutation.isLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
                placeholder="Enter full name"
              />
              {errors.name && (
                <p className="text-xs text-red-600 dark:text-red-400">
                  {errors.name}
                </p>
              )}
            </div>

            {/* Role Field */}
            <div className="space-y-1">
              <label htmlFor="role" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Role *
              </label>
              <select
                id="role"
                name="role"
                value={formData.role}
                onChange={handleInputChange}
                disabled={createInvitationMutation.isLoading}
                className={`w-full px-3 py-2 rounded-md border transition-all duration-200 focus:ring-2 focus:ring-blue-500 focus:outline-none text-gray-900 dark:text-gray-100 bg-white dark:bg-gray-700 ${
                  errors.role 
                    ? 'border-red-300 dark:border-red-500 bg-red-50 dark:bg-red-900/20' 
                    : 'border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500 focus:border-blue-500 dark:focus:border-blue-400'
                } ${createInvitationMutation.isLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                {roles.map((role) => (
                  <option key={role.value} value={role.value}>
                    {role.label}
                  </option>
                ))}
              </select>
              {errors.role && (
                <p className="text-xs text-red-600 dark:text-red-400">
                  {errors.role}
                </p>
              )}
              <p className="text-xs text-gray-500 dark:text-gray-400">
                The user will be assigned this role when they accept the invitation
              </p>
            </div>

            {/* Role Description */}
            <div className="bg-blue-50 dark:bg-blue-900/20 rounded-md p-3 border border-blue-200 dark:border-blue-700">
              <p className="text-xs text-blue-800 dark:text-blue-200">
                <strong>Selected Role:</strong> {roles.find(r => r.value === formData.role)?.label} - {roles.find(r => r.value === formData.role)?.description}
              </p>
            </div>

            {/* General Error */}
            {errors.general && (
              <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-700 rounded-xl p-4">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <div className="w-2 h-2 bg-red-500 rounded-full mt-2"></div>
                  </div>
                  <div className="ml-3">
                    <h3 className="text-sm font-semibold text-red-800 dark:text-red-200">
                      Error
                    </h3>
                    <div className="mt-1 text-sm text-red-700 dark:text-red-300">
                      {errors.general}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Submit Button */}
            <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200 dark:border-gray-700">
              <button
                type="button"
                onClick={() => navigate('/invitations')}
                className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-500 transition-all duration-200"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={createInvitationMutation.isLoading}
                className="px-6 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
              >
                {createInvitationMutation.isLoading ? (
                  <div className="flex items-center justify-center">
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-3"></div>
                    <span>Sending Invitation...</span>
                  </div>
                ) : (
                  'Send Invitation'
                )}
              </button>
            </div>
          </form>
        </div>

        {/* Information Box */}
        <div className="mt-6 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-md p-4">
          <div className="flex items-start">
            <EnvelopeIcon className="h-4 w-4 text-blue-500 mt-0.5 mr-2 flex-shrink-0" />
            <div>
              <h3 className="text-sm font-medium text-blue-900 dark:text-blue-100 mb-2">
                What happens next?
              </h3>
              <div className="space-y-1 text-xs text-blue-800 dark:text-blue-200">
                <div>• An invitation email will be sent to the provided email address</div>
                <div>• The invitation will expire in 24 hours</div>
                <div>• The user can accept the invitation by clicking the link in the email</div>
                <div>• Upon acceptance, a new user account will be created with the selected role</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InvitationForm; 